import type { Asset } from './api.js';

/**
 * What the grid was showing when you clicked into a photo.
 *
 * Two things depend on remembering it, and neither works without it.
 *
 * The viewer needs to know what comes next. A photo opened from the grid is
 * part of a sequence -- the sequence the person was just scrolling through --
 * and arrow keys are only meaningful against that. Re-deriving it in the viewer
 * would mean re-running the search, the filters and the sort, and would still
 * get it wrong for a semantic search whose ranking is not reproducible from the
 * URL alone.
 *
 * And going back needs to land where you left. The grid loads sixty at a time,
 * so someone who scrolled to the four hundredth photo and clicked it would, on
 * returning, get the first sixty and the top of the page -- twenty seconds of
 * scrolling to undo one click. Restoring the whole loaded page avoids refetching
 * it as well, which is the difference between instant and a second of blank grid.
 *
 * This is deliberately module state rather than a React context: it has exactly
 * one producer and one consumer, neither renders from it, and a provider would
 * re-render the entire grid every time the viewer moved to the next photo.
 *
 * A full page reload clears it, which is correct. Someone opening a link to a
 * single photo was never in a sequence, and inventing one for them would be a
 * guess.
 */

export interface BrowseSnapshot {
  /** Identifies the view. A different folder or filter is a different sequence. */
  key: string;
  assets: Asset[];
  cursor: string | null;
  hasMore: boolean;
  /** Preserved so the "visually similar results" notice survives a return. */
  searchMode: string;
  /** Scroll offset of the grid's own scroll container, not the window. */
  scrollY: number;
  /** The last photo actually looked at, which is where "back" should land. */
  focusAssetId: string | null;
  savedAt: number;
}

/**
 * How long a snapshot is trusted.
 *
 * Long enough to cover browsing a folder, opening a few photos and coming back.
 * Short enough that a tab left open overnight reloads rather than showing a grid
 * that predates the morning's uploads.
 */
const MAX_AGE_MS = 10 * 60 * 1000;

let snapshot: BrowseSnapshot | null = null;

/**
 * Identifies a view for the purpose of "is this the same sequence?".
 *
 * Built from the folder and the full query string, so changing a filter, the
 * sort order or the search text starts a new sequence rather than silently
 * reusing the old one's neighbours.
 */
export function browseKey(folderId: string | undefined, search: string): string {
  const params = new URLSearchParams(search);
  // Sorted, so ?a=1&b=2 and ?b=2&a=1 are recognised as the same view rather
  // than quietly discarding a perfectly good snapshot.
  params.sort();
  // Namespaced, so a folder that somehow had the id "root" could not be
  // mistaken for the root view itself.
  return `${folderId ? `folder:${folderId}` : 'root'}?${params.toString()}`;
}

export function saveBrowseSnapshot(next: Omit<BrowseSnapshot, 'savedAt'>): void {
  snapshot = { ...next, savedAt: Date.now() };
}

/** The snapshot for `key`, if there is a fresh one. */
export function readBrowseSnapshot(key: string): BrowseSnapshot | null {
  if (!snapshot || snapshot.key !== key) return null;
  if (Date.now() - snapshot.savedAt > MAX_AGE_MS) {
    snapshot = null;
    return null;
  }
  return snapshot;
}

/** Records something without disturbing the rest, e.g. which photo is open. */
export function updateBrowseSnapshot(patch: Partial<Omit<BrowseSnapshot, 'key'>>): void {
  if (!snapshot) return;
  snapshot = { ...snapshot, ...patch, savedAt: Date.now() };
}

export function clearBrowseSnapshot(): void {
  snapshot = null;
}

export interface Neighbours {
  index: number;
  total: number;
  previousId: string | null;
  nextId: string | null;
}

/**
 * Where this photo sits in the sequence, if it is in one at all.
 *
 * Returns null rather than a single-item sequence when the asset is not in the
 * snapshot -- a photo reached from a direct link, or from the People page, is
 * genuinely not part of a run, and showing arrows that do nothing is worse than
 * showing none.
 */
export function neighbours(assetId: string): Neighbours | null {
  if (!snapshot) return null;
  if (Date.now() - snapshot.savedAt > MAX_AGE_MS) return null;

  const index = snapshot.assets.findIndex((asset) => asset.id === assetId);
  if (index === -1) return null;

  return {
    index,
    total: snapshot.assets.length,
    previousId: index > 0 ? (snapshot.assets[index - 1]?.id ?? null) : null,
    nextId: index < snapshot.assets.length - 1 ? (snapshot.assets[index + 1]?.id ?? null) : null,
  };
}

/**
 * Drops assets from the snapshot, so deleting from the viewer does not leave
 * the sequence pointing at something that is gone.
 */
export function forgetAssets(ids: string[]): void {
  if (!snapshot) return;
  const removed = new Set(ids);
  snapshot = {
    ...snapshot,
    assets: snapshot.assets.filter((asset) => !removed.has(asset.id)),
    focusAssetId:
      snapshot.focusAssetId && removed.has(snapshot.focusAssetId) ? null : snapshot.focusAssetId,
  };
}
